import { getBookingPages } from '@/utils/booking_pages'
import { buildMapItems } from '@/utils/map_pages'
import { vsmRouteName } from '@/utils/vsm_access'
import {
  buildReportContext,
  getAllReportCards,
  getTeaserReportCards,
} from '@/utils/report_pages'

function pageId(page) {
  if (typeof page === 'string') return page
  if (page.path) return 'path:' + page.path
  const q = page.query ? JSON.stringify(page.query) : ''
  return (page.route || page.id || '') + q
}

function card(page, ctx, opts = {}) {
  const accessible = opts.accessible !== undefined ? opts.accessible : true
  return {
    id: pageId(page),
    name: page.name,
    description: page.description || '',
    icon: page.icon || 'fa-file-o',
    route: page.route,
    query: page.query,
    path: page.path,
    external: !!page.external,
    accessible,
    lockedReason: accessible ? null : (opts.lockedReason || 'Not enabled for your account'),
    // 'package' = the tenant has not bought/enabled it (sellable);
    // 'role' = the tenant has it but this user's role is excluded
    // (nothing to sell -- their admin grants access)
    lockType: accessible ? null : (opts.lockType || 'package'),
    // page-level coming soon: clock glyph, no lock, no request cta
    comingSoon: !!opts.comingSoon,
  }
}

function featureLocked(featureEnabled, label) {
  return {
    accessible: !!featureEnabled,
    lockedReason: featureEnabled ? null : `Requires ${label}`,
  }
}

function roleAllows(ctx, roles) {
  if (ctx.isEffectiveAdmin) return true
  return roles.includes(ctx.user.role)
}

// mirrors hasReportsAccess in report_pages.js: these roles never get reports
// even when the tenant pays for them, so their lock is a role lock, not a
// missing package
function reportsRoleLockedFor(ctx) {
  return ['dispatch', 'driver', 'booking-customer', 'customer-care', 'wash-bay'].includes(ctx.user.role)
    || (ctx.user.role === 'garage' && ctx.isTbn)
}

// the live boards ride the reports package but live in the dispatch section,
// mirroring the sidebar's dispatch > boards group
const DISPATCH_BOARD_ROUTES = [
  'driver_board', 'trip_sheet', 'trip_sheet_tbn', 'dispatch_board_tbn',
  'booking_monthly_view_tbn', 'bus_tracker', 'arrivals_departures_board',
]
const DISPATCH_BOARD_ICONS = {
  driver_board: 'fa-users',
  trip_sheet: 'fa-sign-out',
  trip_sheet_tbn: 'fa-sign-out',
  dispatch_board_tbn: 'fa-columns',
  booking_monthly_view_tbn: 'fa-calendar',
  bus_tracker: 'fa-bus',
  arrivals_departures_board: 'fa-exchange',
}

// routes that render as reports-shell pages but belong to other site map
// sections now (boards -> dispatch, vsm rows -> vehicle status)
const NON_REPORT_ROUTES = new Set([
  ...DISPATCH_BOARD_ROUTES, 'vehicle_status_history', 'vehicle_maint_summary',
])

function buildNavContext(user, viewAs) {
  const tenant = user.tenant
  const tenant_type = user.types[tenant]
  const telematics_type = user.telematics_types[tenant]
  const features = user.features[tenant] || {}
  const isEffectiveAdmin = user.role === 'admin' && viewAs !== 'non-admin'
  const isTbn = tenant_type === 'TBN'
  const isCm = tenant_type === 'CM'
  const isCmFleet = tenant_type === 'CM-FLEET'
  const isFasttrak = tenant_type === 'FASTTRAK'
  const isFleet = tenant_type === 'FLEET'
  const isParentTbn = isTbn && tenant.indexOf('parent') !== -1
  const reportCtx = buildReportContext(user, viewAs)

  return {
    user,
    tenant,
    tenant_type,
    telematics_type,
    features,
    isEffectiveAdmin,
    isTbn,
    isCm,
    isCmFleet,
    isFasttrak,
    isFleet,
    isParentTbn,
    reportCtx,
  }
}

function mapSection(ctx) {
  if (ctx.isFleet) {
    return [{
      id: 'maps',
      title: 'Maps',
      subtitle: 'Live fleet visibility.',
      pages: [
        card({ name: 'Fleet Map', route: 'map_fleet', icon: 'fa-map-marker', description: 'Live map of your fleet.' }),
        card({ name: 'Future Map', route: 'map_future', icon: 'fa-clock-o', description: 'Projected vehicle positions and upcoming dispatch coverage.' }),
      ],
    }]
  }

  const mapItems = buildMapItems(ctx.user, ctx.isEffectiveAdmin ? null : 'non-admin')
  const pages = mapItems.map(item => card(item))

  return pages.length ? [{ id: 'maps', title: 'Maps', subtitle: 'Live and historical map views for your fleet.', pages }] : []
}

function bookingsSection(ctx) {
  if (ctx.isFleet || ctx.isCmFleet) return []
  const bookingPages = getBookingPages(ctx.tenant_type)
  if (!bookingPages.length) return []

  const roleOk = roleAllows(ctx, ['admin', 'customer-admin', 'tbn-admin', 'manager', 'customer-care', 'other'])
  const accessible = roleOk
      && (ctx.isEffectiveAdmin || ctx.features.actuals_enabled !== false)

  const pages = bookingPages.map(p => card(p, ctx, {
    accessible: accessible && (ctx.isEffectiveAdmin || ctx.features.actuals_enabled),
    lockedReason: 'Requires Bookings & Actuals processing',
    lockType: roleOk ? 'package' : 'role',
  }))

  return [{ id: 'bookings', title: 'Bookings & Actuals', subtitle: 'Review booking actuals matched against telematics.', pages }]
}

function alertsSection(ctx) {
  if (ctx.user.role === 'booking-customer') return []
  const pages = []

  if (ctx.isCm || ctx.isTbn || ctx.isFasttrak) {
    const alertsOn = !!ctx.features.alerts_enabled
    pages.push(card(
      { name: 'Alerts', route: 'alerts_dashboard', icon: 'fa-bell-o', description: 'Fleet alert lists and delivery status.' },
      ctx,
      featureLocked(alertsOn, 'Alerts'),
    ))
    if (ctx.isCm || ctx.isTbn) {
      const gmsRoute = ctx.isTbn ? 'group_message_sender_tbn' : 'group_message_sender'
      const gmsRoleOk = roleAllows(ctx, ['admin', 'customer-admin', 'tbn-admin', 'manager', 'dispatch'])
      pages.push(card(
        { name: 'Group Messages', route: gmsRoute, icon: 'fa-comments-o', description: 'Send SMS messages to groups of drivers.' },
        ctx,
        {
          accessible: alertsOn && gmsRoleOk,
          lockedReason: 'Requires Alerts',
          lockType: gmsRoleOk ? 'package' : 'role',
        },
      ))
    }
  }

  // sos is its own leaf section in the sidebar; here it joins the alerts
  // section (moved from core). same visibility as before the move.
  if (ctx.features.bus_sos_enabled) {
    pages.push(card(
      { name: 'SOS', route: 'sos_list', icon: 'fa-exclamation-triangle', description: 'Emergency SOS requests from vehicles.' },
      ctx,
      featureLocked(true, 'Bus SOS'),
    ))
  } else if (ctx.isCm || ctx.isTbn) {
    pages.push(card(
      { name: 'SOS', route: 'sos_list', icon: 'fa-exclamation-triangle', description: 'Emergency SOS requests from vehicles.' },
      ctx,
      featureLocked(false, 'Bus SOS'),
    ))
  }

  return pages.length ? [{ id: 'alerts', title: 'Alerts & Notifications', subtitle: 'Notifications, messaging, and emergency SOS for your fleet.', pages }] : []
}

function vsmSection(ctx) {
  if (ctx.isFleet) return []
  const pages = []
  const vsmOn = !!ctx.features.vehicle_status_management_enabled

  if (ctx.isFasttrak) {
    pages.push(card({ name: 'VSM', route: 'vehicle_status_dvir_fasttrak', icon: 'fa-bus', description: 'DVIR and vehicle status workflows.' }))
  } else if (ctx.isCm || ctx.isTbn) {
    const vsmRoute = vsmRouteName(ctx.tenant_type, ctx.features)
    pages.push(card(
      { name: 'VSM', route: vsmRoute, icon: 'fa-bus', description: 'Vehicle inspections, status boards, and yard workflows.' },
      ctx,
      featureLocked(vsmOn, 'Vehicle Status Management'),
    ))
    pages.push(card(
      { name: 'Vehicle Status History', route: 'vehicle_status_history', icon: 'fa-history', description: 'Historical vehicle status and inspection records.' },
      ctx,
      featureLocked(vsmOn, 'Vehicle Status Management'),
    ))
    pages.push(card(
      { name: 'VSM Upcoming', route: ctx.isTbn ? 'vehicle_status_upcoming_tbn' : 'vehicle_status_upcoming', icon: 'fa-calendar', description: 'Upcoming vehicle schedules with wash and maintenance status.' },
      ctx,
      featureLocked(vsmOn, 'Vehicle Status Management'),
    ))
    // moved from the reports list to sit with its vsm family (mirrors the
    // sidebar's vsm section); same audience: cm + the maint flag
    if (ctx.isCm && ctx.features.vehicle_status_management_maint_summary_enabled) {
      pages.push(card(
        { name: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary', icon: 'fa-wrench', description: 'Summary of vehicle maintenance activities and costs.' },
        ctx,
        {
          // showMisc keeps the manager/garage exclusion the old reports-list
          // home enforced
          accessible: ctx.reportCtx.hasReportsAccess && ctx.reportCtx.showMisc,
          lockedReason: 'Requires Reports access',
          lockType: reportsRoleLockedFor(ctx) ? 'role' : 'package',
        },
      ))
    }
  }

  return pages.length ? [{ id: 'vsm', title: 'Vehicle Status Manager', subtitle: 'Inspections, yard status, and maintenance workflows.', pages }] : []
}

// mirrors the sidebar's dispatch section: live boards (rescued from the
// reports list), smart dispatch tools, and driver sync
function dispatchSection(ctx) {
  if (!ctx.isCm && !ctx.isTbn) return []
  if (ctx.user.role === 'booking-customer') return []
  const { reportCtx } = ctx
  const pages = []

  // boards keep the reports-package access logic they had under reports
  const accessibleIds = new Set(getAllReportCards(reportCtx).map(c => pageId(c)))
  const roleLocked = reportsRoleLockedFor(ctx)
  getTeaserReportCards(reportCtx).forEach(c => {
    if (!DISPATCH_BOARD_ROUTES.includes(c.route)) return
    pages.push(card(
      { ...c, icon: DISPATCH_BOARD_ICONS[c.route] || 'fa-columns' },
      ctx,
      {
        accessible: accessibleIds.has(pageId(c)) && reportCtx.hasReportsAccess,
        lockedReason: reportCtx.hasReportsAccess ? 'Available on request' : 'Requires Reports access',
        lockType: roleLocked ? 'role' : 'package',
      },
    ))
  })

  // smart dispatch tools: real when the flag is on; still the coming-soon
  // pitch for cm tenants without it
  const smartOn = !!ctx.features.smart_dispatching_enabled
  if (smartOn) {
    pages.push(card({ name: 'Driver List', route: ctx.isTbn ? 'driver_list_tbn' : 'driver_list', icon: 'fa-users', description: 'Manage the driver list used for smart dispatching.' }))
    pages.push(card({ name: 'Route Assigner', route: ctx.isTbn ? 'smart_route_assignments_tbn' : 'smart_route_assignments', icon: 'fa-random', description: 'Smart route-to-driver assignment suggestions.' }))
    if (ctx.isTbn) {
      pages.push(card({ name: 'Trip Linker', route: 'trip_linker_report_tbn', icon: 'fa-link', description: 'Link related trips for smarter dispatching.' }))
    }
  } else if (ctx.isCm) {
    pages.push(card(
      { name: 'Driver List', route: 'driver_list', icon: 'fa-users', description: 'Manage driver assignments and availability.' },
      ctx,
      { accessible: false, comingSoon: true },
    ))
    pages.push(card(
      { name: 'Route Assigner', route: 'smart_route_assignments', icon: 'fa-random', description: 'Assign routes to drivers based on schedule and availability.' },
      ctx,
      { accessible: false, comingSoon: true },
    ))
  }

  // driver sync (tbn data services). the sidebar gives sync its own
  // top-level section; the site map keeps the cards here as sales-map
  // grouping (dispatch-adjacent driver tooling), not a 1:1 mirror
  if (ctx.isTbn) {
    const syncOn = !!ctx.features.data_services_compare_drivers_enabled
    ;[
      { name: 'Compare Driver Lists', route: 'compare_drivers', icon: 'fa-list-ul', description: 'Compare driver lists between connected systems.' },
      { name: 'Compare Driver Profiles', route: 'compare_driver_profiles', icon: 'fa-id-card-o', description: 'Compare driver profile details between connected systems.' },
      { name: 'Compare Driver History', route: 'compare_drivers_history', icon: 'fa-history', description: 'Past driver comparison runs.' },
    ].forEach(p => pages.push(card(p, ctx, featureLocked(syncOn, 'Data Services'))))
  }

  if (!pages.length) return []
  return [{
    id: 'dispatch',
    title: 'Dispatch',
    subtitle: 'Live dispatch boards and smart dispatching tools.',
    // section-level coming soon only when everything in it is a teaser
    comingSoon: pages.every(p => p.comingSoon),
    pages,
  }]
}

// mirrors the sidebar's ops events section
function opsSection(ctx) {
  if (!ctx.isCm && !ctx.isTbn) return []
  if (ctx.user.role === 'booking-customer') return []
  const roleOk = roleAllows(ctx, ['admin', 'customer-admin', 'tbn-admin'])
  const opsOn = !!ctx.features.ops_desk_enabled
  const pages = [card(
    { name: 'Event Response Manager', route: 'ops_desk', query: { tenant: ctx.tenant }, icon: 'fa-clipboard', description: 'Log and work issues, kudos, and notes with the Huddle recap.' },
    ctx,
    {
      // mirrors the sidebar gate: staff always reach the page, customers
      // need the flag; muggle view-as previews the customer state
      accessible: ctx.isEffectiveAdmin || (roleOk && opsOn),
      lockedReason: 'Requires Event Response Manager',
      lockType: roleOk ? 'package' : 'role',
    },
  )]
  return [{ id: 'ops', title: 'Event Response Manager', subtitle: 'Issue and kudos tracking for your fleet operations.', pages }]
}

function reportsSection(ctx) {
  const { reportCtx } = ctx
  if (!reportCtx.hasReportsAccess && !ctx.isCm && !ctx.isTbn) return []

  const accessibleIds = new Set(getAllReportCards(reportCtx).map(c => pageId(c)))
  const teaserCards = getTeaserReportCards(reportCtx)
  const seen = new Set()
  const pages = []
  const reportsRoleLocked = reportsRoleLockedFor(ctx)

  teaserCards.forEach(c => {
    // boards and vsm rows render in the dispatch / vehicle status sections
    if (NON_REPORT_ROUTES.has(c.route)) return
    const id = pageId(c)
    if (seen.has(id)) return
    seen.add(id)
    const accessible = accessibleIds.has(id) && reportCtx.hasReportsAccess
    pages.push(card(
      { ...c, icon: 'fa-file-text-o' },
      ctx,
      {
        accessible,
        lockedReason: reportCtx.hasReportsAccess ? 'Available on request' : 'Requires Reports access',
        lockType: reportsRoleLocked ? 'role' : 'package',
      },
    ))
  })

  // the sidebar gives Driver Manager its own top-level section; the site
  // map files the cards under reports as sales-map grouping (weekly
  // analytics beside the other reports), not a 1:1 mirror
  const driverManagerOn = !!ctx.features.driver_scorecard_enabled
  // FI-2018 consolidated Driver Scorecard and Scorecard Comparison into
  // Individual Driver / Compare tabs on the Dashboard. FI-2064 rebranded this
  // to Driver Manager and split Individual Driver / Compare back into their
  // own pages, matching the 3-item nav section -- one card each.
  ;[
    { name: 'Driver Manager Dashboard', route: 'driver_manager_dashboard', icon: 'fa-trophy', description: 'Fleet-wide dashboard with score distribution, tier charts, and flagged drivers.' },
    { name: 'Roster List', route: 'driver_manager_roster_list', icon: 'fa-list', description: 'Sortable leaderboard of every driver with tier, combined score, and KPI columns.' },
    { name: 'Individual Driver', route: 'driver_manager_individual', icon: 'fa-user', description: "View a single driver's full KPI profile and PDF report." },
    { name: 'Compare Drivers', route: 'driver_manager_compare', icon: 'fa-columns', description: 'Compare KPIs side-by-side across an arbitrary number of drivers.' },
  ].forEach(p => pages.push(card(p, ctx, featureLocked(driverManagerOn, 'Driver Manager'))))

  pages.unshift(card(
    { name: 'Reports Dashboard', route: 'report_dashboard', icon: 'fa-th-large', description: 'Browse all reports by category.' },
    ctx,
    {
      accessible: reportCtx.hasReportsAccess,
      lockedReason: 'Requires Reports access',
      lockType: reportsRoleLocked ? 'role' : 'package',
    },
  ))

  return pages.length ? [{ id: 'reports', title: 'Reports', subtitle: 'Operational, payroll, on-time, driver manager, and fleet analytics reports.', pages }] : []
}

function coreSection(ctx) {
  const pages = [
    card({ name: 'Dashboard', route: 'dashboard', icon: 'fa-tachometer', description: 'Overview of integration features and processing events.' }),
  ]

  if (ctx.isCm || ctx.isTbn) {
    pages.push(card(
      { name: 'Fleet Assistant', route: 'fleet_assistant', icon: 'fa-commenting', description: 'AI assistant for fleet data, help articles, and dispatch questions.' },
      ctx,
      featureLocked(ctx.features.fleet_assistant_enabled !== false, 'Fleet Assistant'),
    ))
  }

  if (ctx.isEffectiveAdmin || ctx.features.help_center_enabled !== false) {
    pages.push(card({ name: 'Help Center', route: 'help_center', icon: 'fa-question-circle', description: 'Help articles, company docs, and support tickets.' }))
    // help_center's own tabs (nav/registry.js's help_center/help_admin
    // groups) had no site map presence beyond the generic tile above --
    // pull the ones people actually go looking for out on their own,
    // mirroring how Manage Users is pulled out of Settings below. queries
    // match the registry's own exactly, so favoriting these resolves in
    // the sidebar/favorites bar with no alias needed.
    pages.push(card({ name: 'Support Ticket Manager', route: 'help_center', query: { tab: 'support' }, icon: 'fa-ticket', description: 'Open and track support requests.' }))
    if (ctx.isEffectiveAdmin || ctx.features.company_documents_in_help_center !== false) {
      pages.push(card({ name: 'Company Docs', route: 'help_center', query: { tab: 'company' }, icon: 'fa-file-text-o', description: 'Documents your company shares with portal users.' }))
    }
    if (ctx.isEffectiveAdmin) {
      pages.push(card({ name: 'Feedback Review', route: 'help_center', query: { tab: 'feedback' }, icon: 'fa-comments-o', description: 'Review feedback left on assistant answers.' }))
      pages.push(card({ name: 'Assistant Insights', route: 'help_center', query: { tab: 'insights' }, icon: 'fa-lightbulb-o', description: 'Fleet Assistant usage and cost across tenants.' }))
      pages.push(card({ name: 'Announcements', route: 'help_center', query: { tab: 'announcements' }, icon: 'fa-bullhorn', description: 'Compose and publish product announcements.' }))
    }
  }

  pages.push(card({ name: 'Notifications', route: 'notifications_list', icon: 'fa-bell', description: 'Your in-app notifications and announcements.' }))

  if (roleAllows(ctx, ['admin', 'customer-admin', 'tbn-admin'])) {
    pages.push(card({ name: 'Settings', route: 'settings', icon: 'fa-cog', description: 'Tenant configuration, alerts, reports, and integrations.' }))
    pages.push(card({ name: 'Manage Users', route: 'user_list', icon: 'fa-user-o', description: 'Add and manage portal users for your account.' }))
  }

  if (ctx.isEffectiveAdmin) {
    pages.push(card({ name: 'Admin Tools', route: 'admin_tools_dashboard', icon: 'fa-wrench', description: 'System health, process manager, and debugging tools.' }))
    pages.push(card({ name: 'API Usage', route: 'api_usage_dashboard', icon: 'fa-bar-chart', description: 'API request volumes and usage breakdowns.' }))
    pages.push(card({ name: 'SMS Usage', route: 'sms_usage_dashboard', icon: 'fa-comment-o', description: 'SMS volumes, delivery status, and carrier costs.' }))
  }

  return [{ id: 'core', title: 'Core Integration Tools', subtitle: 'Dashboard, help, Fleet Assistant, settings, admin tools, and notifications.', pages }]
}

// only people who can actually buy/authorize features should see the
// request CTAs (staff admins keep them so they can preview the customer
// experience); everyone else just sees the locked state
export function canRequestFeatures(user, viewAs) {
  const ctx = buildNavContext(user, viewAs)
  return roleAllows(ctx, ['customer-admin', 'tbn-admin', 'manager'])
}

// section order follows the sidebar's arc (core, maps, bookings, then the
// ops surfaces, then reports) without mirroring it 1:1: the sidebar's
// scorecard/sync/sos sections fold into reports/dispatch/alerts here as
// sales-map grouping
export function buildSiteMapSections(user, viewAs) {
  const ctx = buildNavContext(user, viewAs)
  return [
    ...coreSection(ctx),
    ...mapSection(ctx),
    ...bookingsSection(ctx),
    ...vsmSection(ctx),
    ...alertsSection(ctx),
    ...dispatchSection(ctx),
    ...opsSection(ctx),
    ...reportsSection(ctx),
  ].filter(section => section.pages && section.pages.length)
}

export function flattenSiteMapPages(sections) {
  const pages = []
  const seen = new Set()
  sections.forEach(section => {
    section.pages.forEach(page => {
      if (seen.has(page.id)) return
      seen.add(page.id)
      pages.push({ ...page, sectionId: section.id, sectionTitle: section.title })
    })
  })
  return pages
}

// a few site map pages link straight to a standalone/legacy route or a bare
// shell page that the nav registry (nav/registry.js, the horizon sidebar's
// data source) never produces on its own -- every registry row for that
// feature pins a specific tab query instead. the tile itself still navigates
// to the site map's own route unchanged; this only affects what gets stored
// when the star is clicked, so the favorite resolves to the same registry
// destination the sidebar/favorites bar already know how to render. keyed by
// the site map's own bare route name (these all have no query of their own).
const FAVORITE_DESTINATION_ALIASES = {
  // "Manage Users" tile links to the standalone user_list page; the registry
  // only knows this feature as the settings shell's user-list tab
  user_list: { name: 'settings', query: { tab: 'tab_user_list' } },
  // the plain Settings tile has no bare landing in the registry -- its
  // section landing pins the overview tab (registry.js's settings section)
  settings: { name: 'settings', query: { tab: 'tab_overview' } },
  // Help Center likewise has no bare landing; every registry row pins a tab,
  // articles being the first/default one
  help_center: { name: 'help_center', query: { tab: 'articles' } },
}

// pages a star can apply to: real, accessible, internal destinations only.
// external links open elsewhere and locked/coming-soon tiles have nothing a
// favorite could point at yet.
export function favoritableDestination(page) {
  if (!page || !page.accessible || page.external || !page.route) return null
  const alias = !page.query && FAVORITE_DESTINATION_ALIASES[page.route]
  if (alias) return alias
  return { name: page.route, query: page.query }
}

// site map pages shaped like the registry destinations the favorites surfaces
// render (fi_favorites_bar.vue, and the rail's Favorites section), so they can
// be handed to resolveFavorites as a SECOND source behind the registry.
//
// the registry stays authoritative -- it owns the rail and its labels, icons
// and gates are what those surfaces are built from. this covers the gap the
// two surfaces have always had between them: the site map offers a star on
// every page it lists, and for any page the registry has no destination for
// (Fleet Assistant, the scorecard pages, and for some roles a dozen more rows
// the site map lists but that user's rail does not), the star saved fine and
// then rendered nowhere, on any surface. the fallback is built per call from
// the same sections, so gates re-apply exactly as they do for registry rows
// and a favorite for a page the user has lost simply stops appearing.
export function siteMapFavoriteDestinations(user, viewAs) {
  const out = []
  flattenSiteMapPages(buildSiteMapSections(user, viewAs)).forEach(page => {
    const to = favoritableDestination(page)
    if (!to) return
    out.push({
      // namespaced so it can never collide with a registry item's structural
      // 'section:group:item' key, which is what both surfaces use as a :key
      key: 'sitemap:' + page.id,
      label: page.name,
      icon: page.icon,
      desc: page.description || '',
      to,
      sectionLabel: page.sectionTitle,
    })
  })
  return out
}

// the Favorites section pages, in the order the user starred them (mirrors
// resolveFavorites in nav/registry.js). gates are re-applied via `sections`
// on every call, so a favorite for a page the user has lost access to just
// stops appearing here -- same rule as the sidebar rail. `keyFor` is passed
// in (rather than imported) so this stays decoupled from the favorites
// persistence module, matching resolveFavorites' own signature.
export function buildFavoritesSection(sections, favKeys, keyFor) {
  if (!favKeys || !favKeys.length) return null
  const byKey = new Map()
  flattenSiteMapPages(sections).forEach(page => {
    const dest = favoritableDestination(page)
    const key = dest && keyFor(dest)
    if (key && !byKey.has(key)) byKey.set(key, page)
  })
  const pages = []
  const seen = new Set()
  favKeys.forEach(key => {
    if (seen.has(key)) return
    seen.add(key)
    const page = byKey.get(key)
    if (page) pages.push(page)
  })
  if (!pages.length) return null
  return {
    id: 'favorites',
    title: 'Favorites',
    // no subtitle: "the pages you have starred" is redundant under a
    // section literally titled Favorites
    pages,
  }
}
